<template>
  <div>
    <!-- Standard Permission Check -->
    <div class="row" v-if="userData.permissions['view_settings'] == 1">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">

            <!-- A single row to hold the 3-column grid -->
            <div class="row">

              <!-- Iterate over documents using col-lg-4 for 3 columns per row -->
              <div v-for="doc in documents" :key="doc.id" class="col-md-6 col-lg-4 mb-4">
                <div class="border rounded p-3 h-100">
                  <h5 class="card-title mb-1">{{ doc.title }}</h5>

                  <!-- State 1: Document Saved View -->
                  <div v-if="doc.lastUpdated && !doc.selectedFile"
                    class="alert d-flex flex-column justify-content-between h-100 py-3 mb-0">
                    <div class="mb-3">
                      <i class="mdi mdi-file-document text-success mr-2" style="font-size: 20px;"></i>
                      <!-- Changed to a clickable action for secure download -->
                      <a href="#" @click.prevent="downloadDocument(doc)" class="text-success text-decoration-underline"
                        style="cursor: pointer;">
                        <strong>{{ doc.savedName }}</strong>
                      </a>
                      <div class="text-muted small mt-1">Last updated: {{ doc.lastUpdated }}</div>
                    </div>
                    <div class="d-flex w-100">
                      <b-button class="flex-fill me-2" size="sm" variant="outline-primary"
                        @click="resetDocument(doc)">Replace</b-button>
                      <b-button class="flex-fill" size="sm" variant="outline-danger"
                        @click="deleteDocument(doc)">Delete</b-button>
                    </div>
                  </div>

                  <!-- State 2: File Attached (Pending Save) View -->
                  <div v-else-if="doc.selectedFile" class="mt-2">
                    <div class="alert alert-info py-2 mb-3 text-truncate" :title="doc.selectedFile.name">
                      <i class="mdi mdi-file-pdf text-danger mr-2"></i> {{ doc.selectedFile.name }}
                    </div>

                    <div class="text-end">
                      <b-button @click="cancelUpload(doc)" class="btn btn-sm flex-fill me-2" variant="secondary">
                        <i class="mdi mdi-close"></i> Cancel
                      </b-button>
                      <b-button @click="uploadDocument(doc)" class="btn btn-sm flex-fill" variant="primary">
                        <i class="mdi mdi-content-save"></i> Save
                      </b-button>
                    </div>
                  </div>

                  <!-- State 3: Compact Dropzone View -->
                  <div v-else class="upload-dropzone h-100 d-flex flex-column justify-content-center"
                    @click="triggerFileInput(doc.id)" @dragover.prevent @dragenter.prevent
                    @drop.prevent="handleDrop($event, doc)">
                    <input type="file" accept="application/pdf" :ref="'fileInput_' + doc.id" class="d-none"
                      @change="handleFileChange($event, doc)" />
                    <i class="mdi mdi-cloud-upload-outline dropzone-icon"></i>
                    <h5 class="dropzone-title">Click or drag file here</h5>
                    <p class="dropzone-text mt-1">Maximum file size 5MB. <span class="dropzone-pdf">PDF Only</span></p>
                  </div>

                </div>
              </div>

            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- No Permission View -->
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div>
            <img src="@/assets/images/profile-img.png" alt class="img-fluid" /><br><br>
            <center><span class="text-primary h6">You have no permission to View the Company Settings</span></center>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Document Upload",
    meta: [{ name: "description", content: appConfig.description }]
  },

  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")) || { permissions: {} },
      maxFileSize: 5 * 1024 * 1024,

      // Expandable list of documents
      documents: [
        {
          id: 'rules_constitution',
          title: 'Rules, Regulations, Policies & Constitution',
          selectedFile: null,
          savedName: '',
          lastUpdated: null
        }
      ]
    };
  },

  mounted() {
    this.fetchDocuments();
  },

  methods: {
    async fetchDocuments() {
      try {
        let url = "/get-sacco-documents";
        const response = await axios.get(url);

        if (response.status === 200) {
          const fetchedDocs = response.data.documents;

          this.documents.forEach(doc => {
            if (fetchedDocs[doc.id]) {
              doc.savedName = doc.title;
              doc.lastUpdated = fetchedDocs[doc.id].last_updated;
            }
          });
        }
      } catch (_) {
        this.$toast.error("Failed to fetch documents. Please try again later.");
      }
    },

    async downloadDocument(doc) {
      this.$toast.success("Downloading document...");
      try {
        const response = await axios.get(`/download-sacco-document/${doc.id}`, {
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        let fileName = `${doc.title}.pdf`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error("Failed to download document. It may have been removed.");
      }
    },

    triggerFileInput(docId) {
      const inputRef = this.$refs['fileInput_' + docId];
      if (inputRef) {
        const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
        input.click();
      }
    },

    handleDrop(event, doc) {
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.validateAndSetFile(files[0], doc);
      }
    },

    handleFileChange(event, doc) {
      const files = event.target.files;
      if (files.length > 0) {
        this.validateAndSetFile(files[0], doc);
      }
    },

    validateAndSetFile(file, doc) {
      if (file.type !== "application/pdf") {
        this.$toast.error("Invalid file format. Please upload a PDF file only.");
        return;
      }

      if (file.size > this.maxFileSize) {
        this.$toast.error("File is too large. Maximum file size is 5MB.");
        return;
      }

      doc.selectedFile = file;
    },

    cancelUpload(doc) {
      doc.selectedFile = null;

      const inputRef = this.$refs['fileInput_' + doc.id];
      if (inputRef) {
        const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
        input.value = "";
      }
    },

    resetDocument(doc) {
      doc.savedName = '';
      doc.lastUpdated = null;
      this.cancelUpload(doc);
    },

    // NEW: Delete Implementation using BootstrapVue's native msgBoxConfirm
    async deleteDocument(doc) {
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(`Are you sure you want to delete the ${doc.title}?`, {
          title: 'Confirm Deletion',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2 border-top-0',
          hideHeaderClose: false,
          centered: true
        });

        if (confirmed) {
          const response = await axios.post(`/delete-sacco-document/${doc.id}`);

          if (response.status === 200) {
            this.$toast.success("Document deleted successfully.");
            this.resetDocument(doc);
          } else {
            // Handle cases where the server returned a weird status like 204
            this.$toast.warning("Document deletion state unknown.");
          }
        }
      } catch (error) {
        // Only show the error toast if it was an ACTUAL HTTP error from the server
        if (error.response) {
          this.$toast.error(error.response.data.message || "An error occurred while deleting the document.");
        }
        // Ignore errors caused by the user closing the modal or component unmounting
      }
    },

    async uploadDocument(doc) {
      if (!doc.selectedFile) {
        this.$toast.error("Please select a file to upload.");
        return;
      }

      let formData = new FormData();
      formData.append("document", doc.selectedFile);
      formData.append("document_id", doc.id);

      let url = "/upload-sacco-document";

      try {
        const response = await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        if (response.status === 200 || response.status === 201) {
          doc.savedName = doc.title;
          doc.lastUpdated = new Date().toLocaleString();
          doc.selectedFile = null;

          this.fetchDocuments();

          this.$toast.success(`${doc.title} saved successfully.`);
        }
      } catch (error) {
        this.$toast.error("An error occurred while uploading the document.");
      }
    }
  }
};
</script>

<style scoped>
/* Styling updated to create a more compact, reduced dropzone */
.upload-dropzone {
  border: 2px dashed #cfd4d9;
  border-radius: 8px;
  padding: 20px 10px;
  text-align: center;
  cursor: pointer;
  background-color: #ffffff;
  transition: all 0.3s ease;
}

.upload-dropzone:hover {
  background-color: #f8f9fa;
  border-color: #aeb4b9;
}

.dropzone-icon {
  font-size: 32px;
  color: #5b626b;
  margin-bottom: 10px;
}

.dropzone-title {
  font-size: 1.1rem;
  color: #1f3a52;
  margin-bottom: 5px;
  line-height: 1.2;
}

.dropzone-text {
  font-size: 0.85rem;
  color: #2c3e50;
  margin-bottom: 0px;
}

.dropzone-pdf {
  font-size: 0.85rem;
  color: #007bff;
  font-weight: 500;
}
</style>